import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { SocketStatus } from '../../../data-models/socket-base.data-model';
import { HudsonWsService } from '../../../services/HudsonWsService';
import { queryClient } from '../../../services/MaggieQueryClient';
import { customerTenantIdState } from '../../../services/state/AuthState';
import { AgentWsIdentityAtom, ChatConversationsQueryKey } from './chatActionHooks';

/**
 * A socket that connected before the customer was known bound itself to the token's tenant,
 * and the conversation list it delivered belongs there too; once the customer is known, the
 * socket reconnects. Whoever owns the socket's lifecycle runs this (`enabled`), so one tenant
 * change is one reconnect.
 */
export function useReconnectOnTenantChange(enabled: boolean): void {
  const customerTenantId = useAtomValue(customerTenantIdState);
  const setAgentWsIdentity = useSetAtom(AgentWsIdentityAtom);
  const lastTenantIdRef = useRef(customerTenantId);

  useEffect(() => {
    const previous = lastTenantIdRef.current;
    lastTenantIdRef.current = customerTenantId;
    if (!enabled || !customerTenantId || customerTenantId === previous) {
      return;
    }
    const service = HudsonWsService.get();
    if (!service || service.socketStatusSync === SocketStatus.closed) {
      return;
    }
    // The old identity names the token's tenant; drop it so the mismatch banner
    // waits for the new handshake instead of flashing during the reconnect
    setAgentWsIdentity(null);
    void service.reconnect();
    queryClient.invalidateQueries({ queryKey: [ChatConversationsQueryKey] });
  }, [customerTenantId, enabled, setAgentWsIdentity]);
}
